import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Stack, Typography,
} from '@mui/material';
import { Search, VerifiedUserOutlined } from '@mui/icons-material';
import messagingService from '../../services/messagingService';
import { useAuth } from '../../contexts/AuthContext';
import { brand, text } from '../../theme/colors';

export const INK = '#1e293b';
// Role and status tags must read at a glance against a white card (owner,
// 2026-09-28: "Conveyancer, Notary public, Partner should have contrast").
export const TAG_SX = { bgcolor: '#e6f4f2', color: '#134e4a', border: '1px solid #9fcfc6', fontWeight: 700, borderRadius: '6px' };
const TEAL_DEEP = '#134e4a';

export const fmtChecked = (iso) => {
  if (!iso) return 'checked';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'checked today';
  if (days === 1) return 'checked yesterday';
  return `checked ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** "Message" opens (or reuses) a chat; signed out, it goes to sign-up first. */
export function useMessage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const message = async (userId) => {
    if (!isAuthenticated) {
      navigate(`/register?next=${encodeURIComponent(location.pathname + location.search)}`, { state: { from: location } });
      return;
    }
    setBusy(userId); setError('');
    try {
      const res = await messagingService.getOrCreateConversation(userId);
      const id = res?.data?.id || res?.data?.conversation?.id || res?.id;
      navigate(id ? `/messages?conversation=${id}` : '/messages');
    } catch (e) {
      setError(e.response?.data?.message || 'We could not open the conversation. Please try again.');
    } finally { setBusy(null); }
  };
  return { message, busy, error, clearError: () => setError('') };
}

/** One member card: avatar, name, firm, the standing badge, details, two buttons. */
export function ProCard({ name, firm, photo, badge, details = [], chips = [], primary, secondary }) {
  return (
    <Box component="article" sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 0 0 1px #dfe6e4', p: 2.75, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        {photo ? (
          <Box component="img" src={photo} alt={name} sx={{ width: 72, height: 72, flexShrink: 0, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 0 0 2px #fff, 0 0 0 3px #dfe6e4' }} />
        ) : (
          <Box aria-hidden sx={{ width: 72, height: 72, flexShrink: 0, borderRadius: '50%', bgcolor: TEAL_DEEP, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700 }}>
            {initials(name)}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700, color: INK }}>{name}</Typography>
          {firm && <Typography sx={{ fontSize: 15, color: text.secondary }}>{firm}</Typography>}
        </Box>
      </Stack>
      <Chip
        icon={<VerifiedUserOutlined sx={{ color: `${TEAL_DEEP} !important` }} />}
        label={badge}
        sx={{ alignSelf: 'flex-start', bgcolor: '#e6f4f2', color: TEAL_DEEP, fontWeight: 700, borderRadius: '8px', height: 32 }}
      />
      {chips.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {chips.map((c) => <Chip key={c} label={c} size="small" sx={TAG_SX} />)}
        </Stack>
      )}
      <Stack spacing={0.5}>
        {details.filter(Boolean).map((d) => <Typography key={d} sx={{ fontSize: 15, color: d.startsWith('**') ? INK : text.secondary }}>{d.replace(/^\*\*/, '')}</Typography>)}
      </Stack>
      <Stack direction="row" spacing={1.25}>
        {primary && (
          <Button fullWidth onClick={primary.onClick} disabled={primary.disabled}
            sx={{ height: 46, borderRadius: '10px', bgcolor: brand.primaryDark, color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 15, '&:hover': { bgcolor: TEAL_DEEP } }}>
            {primary.label}
          </Button>
        )}
        {secondary && (
          <Button fullWidth onClick={secondary.onClick} disabled={secondary.disabled}
            sx={{ height: 46, borderRadius: '10px', border: `1.5px solid ${brand.primaryDark}`, color: brand.primaryDark, textTransform: 'none', fontWeight: 700, fontSize: 15 }}>
            {secondary.label}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

/**
 * The shared page frame: eyebrow, headline, lead, optional explainer, the
 * search row, then results on the left and a sidebar on the right.
 */
export default function DirectoryLayout({
  eyebrow, title, lead, explainer, searchRow, resultsTitle, resultsNote, loading, error, empty, children, after, sidebar, footnote,
}) {
  return (
    <Box sx={{ bgcolor: '#f6f9f8', minHeight: '100vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe6e4' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4.5 } }}>
          <Stack spacing={1.25} sx={{ maxWidth: 860 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.primaryDark }}>{eyebrow}</Typography>
            <Typography component="h1" sx={{ fontSize: { xs: 32, md: 44 }, fontWeight: 800, lineHeight: 1.1, color: INK, letterSpacing: '-0.015em', textWrap: 'balance' }}>{title}</Typography>
            <Typography sx={{ fontSize: { xs: 16, md: 18 }, lineHeight: 1.55, color: text.secondary }}>{lead}</Typography>
          </Stack>
          {explainer && <Box sx={{ mt: 3 }}>{explainer}</Box>}
          <Box sx={{ mt: 3 }}>{searchRow}</Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' }, gap: 4.5, alignItems: 'start' }}>
          <Box>
            <Stack direction="row" alignItems="baseline" spacing={1.75} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
              <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700, color: INK }}>{resultsTitle}</Typography>
              {resultsNote && <Typography sx={{ fontSize: 15, color: text.secondary }}>{resultsNote}</Typography>}
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
              <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} aria-label="Loading" /></Box>
            ) : (
              <>
                {empty}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25 }}>{children}</Box>
                {after}
              </>
            )}
            {footnote && <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: text.secondary, mt: 3 }}>{footnote}</Typography>}
          </Box>
          <Stack spacing={2.25}>{sidebar}</Stack>
        </Box>
      </Container>
    </Box>
  );
}

/** The search row: a wide box plus any selects, and a Search button. */
export function DirectorySearch({ value, onChange, onSubmit, placeholder, children }) {
  return (
    <Box component="form" role="search" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      <Box sx={{ flex: '1 1 320px', minWidth: 0, height: 54, display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, border: '1.5px solid #94a3b8', borderRadius: '10px', bgcolor: '#fff', '&:focus-within': { borderColor: INK } }}>
        <Search sx={{ color: text.secondary }} />
        <Box component="input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
          sx={{ flex: 1, minWidth: 0, border: 0, outline: 'none', fontSize: 17, fontFamily: 'inherit', bgcolor: 'transparent', color: INK }} />
      </Box>
      {children}
      <Button type="submit" sx={{ height: 54, px: 3.5, borderRadius: '10px', bgcolor: brand.primaryDark, color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 16, '&:hover': { bgcolor: TEAL_DEEP } }}>
        Search
      </Button>
    </Box>
  );
}

/** Dark sidebar card ("Before you pay a deposit"). */
export function DarkCard({ title, children }) {
  return (
    <Box sx={{ bgcolor: TEAL_DEEP, color: '#fff', borderRadius: 2, p: 3 }}>
      <Typography component="h2" sx={{ fontSize: 24, fontWeight: 800, mb: 1.5, lineHeight: 1.2 }}>{title}</Typography>
      {children}
    </Box>
  );
}

/** Light sidebar card. */
export function LightCard({ title, children }) {
  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 0 0 1px #dfe6e4', p: 2.75 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: INK, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}
